import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.ts';
import { guardarSeleccion } from '../lib/catalogo.ts';
import { descargarArchivo } from '../lib/descargas.ts';
import { Boton, Cargando, Chip, EncabezadoPagina } from '../componentes/ui.tsx';
import { BannerCampania } from '../componentes/BannerCampania.tsx';

interface Material {
  id: string;
  titulo: string;
  tipo: 'IMAGEN' | 'VIDEO';
  marcaNombre: string;
  productoId: string | null;
  referencia: string | null;
  url: string;
  posterUrl: string | null;
  puedePersonalizar: boolean;
}

/** Galería (D34): pestañas Imágenes/Videos, filtros, personalizar y descargar. */
export function PaginaGaleria() {
  const { t } = useTranslation();
  const navegar = useNavigate();
  const [tipo, setTipo] = useState<'IMAGEN' | 'VIDEO'>('IMAGEN');
  const [filtro, setFiltro] = useState<'todo' | 'comprados' | 'generales'>('todo');
  const [marcaFiltro, setMarcaFiltro] = useState('');

  const materiales = useInfiniteQuery({
    queryKey: ['galeria', tipo, filtro],
    queryFn: ({ pageParam }) =>
      api<{ items: Material[]; siguienteCursor: string | null }>(
        `/galeria?tipo=${tipo}&filtro=${filtro}&limit=30${pageParam ? `&cursor=${pageParam}` : ''}`,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.siguienteCursor ?? undefined,
  });

  const todas = materiales.data?.pages.flatMap((p) => p.items) ?? [];
  const marcasDisponibles = [...new Set(todas.map((m) => m.marcaNombre))].sort();
  const filas = marcaFiltro ? todas.filter((m) => m.marcaNombre === marcaFiltro) : todas;

  function personalizar(material: Material) {
    if (!material.productoId) return;
    // La imagen ligada a producto abre el editor con plantilla PERSONALIZAR_IMAGEN (§6.7).
    guardarSeleccion({
      productoIds: [material.productoId],
      tipoContenido: 'PERSONALIZAR_IMAGEN',
      materialId: material.id,
      materialUrl: material.url,
    });
    void navegar({ to: '/plantillas' });
  }

  async function descargar(material: Material) {
    try {
      const resp = await fetch(material.url);
      if (!resp.ok) throw new Error(String(resp.status));
      descargarArchivo(
        `${material.titulo.replace(/[^\w.-]+/g, '-')}.${material.tipo === 'VIDEO' ? 'mp4' : 'png'}`,
        await resp.blob(),
      );
    } catch {
      // Sin CORS del almacén de archivos: al menos abrirlo para guardar manualmente.
      window.open(material.url, '_blank', 'noopener');
    }
  }

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina
        titulo={t('galeria.titulo')}
        acciones={(['IMAGEN', 'VIDEO'] as const).map((valor) => (
          <Chip key={valor} activo={tipo === valor} onClick={() => setTipo(valor)}>
            {valor === 'IMAGEN' ? t('galeria.imagenes') : t('galeria.videos')}
          </Chip>
        ))}
      />

      <BannerCampania seccion="galeria_h" />

      <div className="flex flex-wrap items-center gap-2">
        {(
          [
            ['todo', t('galeria.verTodo')],
            ['comprados', t('galeria.deMisCompras')],
            ['generales', t('galeria.generales')],
          ] as const
        ).map(([valor, etiqueta]) => (
          <Chip key={valor} activo={filtro === valor} onClick={() => setFiltro(valor)}>
            {etiqueta}
          </Chip>
        ))}
        {marcasDisponibles.length > 1 ? <span className="mx-1 h-5 w-px bg-superficie2" /> : null}
        {marcasDisponibles.length > 1
          ? marcasDisponibles.map((nombre) => (
              <Chip key={nombre} activo={marcaFiltro === nombre} onClick={() => setMarcaFiltro(marcaFiltro === nombre ? '' : nombre)}>
                {nombre}
              </Chip>
            ))
          : null}
      </div>

      {materiales.isPending ? <Cargando texto={t('comun.cargando')} /> : null}

      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
        {filas.map((m) => (
          <li key={m.id} className="overflow-hidden tarjeta">
            {m.tipo === 'VIDEO' ? (
              <video
                src={m.url}
                poster={m.posterUrl ?? undefined}
                controls
                preload="metadata"
                playsInline
                className="aspect-[4/5] w-full rounded-[20px] bg-black object-contain"
              />
            ) : (
              <img src={m.url} alt={m.titulo} loading="lazy" className="aspect-[4/5] w-full rounded-[20px] object-cover" />
            )}
            <div className="space-y-1 p-3">
              <p className="truncate text-[15px] font-medium">{m.titulo}</p>
              <p className="text-xs text-suave">
                {m.marcaNombre}
                {m.referencia ? ` · ${m.referencia}` : ` · ${t('galeria.general')}`}
              </p>
              <div className="flex gap-2 pt-1">
                {m.puedePersonalizar ? (
                  <Boton onClick={() => personalizar(m)}>{t('galeria.personalizar')}</Boton>
                ) : null}
                <Boton variante="secundario" onClick={() => void descargar(m)}>
                  {t('galeria.descargar')}
                </Boton>
              </div>
            </div>
          </li>
        ))}
      </ul>
      {filas.length === 0 && !materiales.isPending ? (
        <p className="py-10 text-center text-sm text-suave">{t('galeria.vacio')}</p>
      ) : null}
      {materiales.hasNextPage ? (
        <Boton variante="secundario" onClick={() => void materiales.fetchNextPage()}>
          {t('galeria.mas')}
        </Boton>
      ) : null}
    </main>
  );
}
